/**
 * useMultiSelect — Shift+Click multi-selection for the Fluid Engine
 * (Iter 147 Path A · Session 3).
 *
 * Tracks a set of `(blockId, field)` keys. Click an element to single-
 * select; Shift+Click to add/remove from selection. Selected elements
 * receive a `data-wb-selected="1"` attribute that the global CSS
 * decorates with a blue outline + handle ring.
 *
 * The hook also returns `expandDragSiblings(field, dCol, dRow)` so
 * `useFluidTextDrag` can group-move every other selected element by the
 * same delta when one of them is dragged.
 */
import { useEffect, useRef, useState, useCallback } from 'react';

const SEL_STYLE_ID = 'wb-multi-select-style';

function ensureSelStyle() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(SEL_STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = SEL_STYLE_ID;
  el.textContent = `
    [data-wb-selected="1"] {
      outline: 2px solid #3B82F6 !important;
      outline-offset: 4px;
      border-radius: 6px;
    }
    .wb-multi-select-toolbar {
      position: fixed;
      z-index: 9000;
      background: #0E0E23;
      color: #fff;
      border-radius: 999px;
      padding: 6px 12px;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      box-shadow: 0 8px 24px rgba(14,14,35,0.45);
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.4px;
    }
    .wb-multi-select-toolbar button {
      background: rgba(255,255,255,0.08);
      color: #fff;
      border: 0;
      border-radius: 6px;
      padding: 4px 8px;
      cursor: pointer;
      font-size: 11px;
      transition: background 0.15s ease;
    }
    .wb-multi-select-toolbar button:hover { background: rgba(255,255,255,0.18); }
    .wb-multi-select-toolbar .sep { width: 1px; height: 14px; background: rgba(255,255,255,0.18); margin: 0 2px; }
  `;
  document.head.appendChild(el);
}

const keyOf = (el) => {
  const blockEl = el.closest('[data-block-id]');
  if (!blockEl) return null;
  const blockId = blockEl.getAttribute('data-block-id');
  let field;
  if (el.matches('[data-testid^="inline-"]')) {
    field = el.getAttribute('data-testid').replace(`inline-${blockId}-`, '');
  } else if (el.matches('[data-wb-image="1"]')) {
    field = `__image_${el.getAttribute('data-wb-image-index')}`;
  } else if (el.matches('[data-wb-button="1"]')) {
    const bField = el.getAttribute('data-wb-button-field') || 'buttons';
    field = `__btn_${bField}_${el.getAttribute('data-wb-button-index')}`;
  } else {
    return null;
  }
  return `${blockId}|${field}`;
};

const elFromKey = (key) => {
  const [blockId, field] = key.split('|');
  const sect = document.querySelector(`[data-block-id="${blockId}"]`);
  if (!sect) return null;
  if (field.startsWith('__image_')) {
    return sect.querySelectorAll('[data-wb-image="1"]')[parseInt(field.split('_')[2], 10)];
  }
  if (field.startsWith('__btn_')) {
    const rest = field.slice(6);
    const lastU = rest.lastIndexOf('_');
    const bField = rest.slice(0, lastU);
    const idx = parseInt(rest.slice(lastU + 1), 10);
    return sect.querySelectorAll(`[data-wb-button="1"][data-wb-button-field="${bField}"]`)[idx];
  }
  return sect.querySelector(`[data-testid="inline-${blockId}-${field}"]`);
};

export function useMultiSelect({ enabled = true } = {}) {
  const [selected, setSelected] = useState(() => new Set());
  const selectedRef = useRef(selected);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  useEffect(() => { ensureSelStyle(); }, []);

  // Apply data-wb-selected attribute on every selected element + clear
  // it on every previously-selected element.
  useEffect(() => {
    document.querySelectorAll('[data-wb-selected="1"]').forEach(n => n.removeAttribute('data-wb-selected'));
    selected.forEach(key => {
      const el = elFromKey(key);
      if (el) el.setAttribute('data-wb-selected', '1');
    });
  }, [selected]);

  // Document-level Shift+Click listener.
  useEffect(() => {
    if (!enabled) return;
    const onClick = (e) => {
      if (!e.shiftKey) return;
      const target = e.target?.closest?.('[data-testid^="inline-"], [data-wb-image="1"], [data-wb-button="1"]');
      if (!target) return;
      const key = keyOf(target);
      if (!key) return;
      e.preventDefault();
      e.stopPropagation();
      setSelected(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    };
    // Clear selection when clicking on an empty area (no element + no shift)
    const onPlainClick = (e) => {
      if (e.shiftKey) return;
      if (e.target?.closest?.('[data-testid^="inline-"], [data-wb-image="1"], [data-wb-button="1"], .wb-multi-select-toolbar')) return;
      if (selectedRef.current.size > 0) setSelected(new Set());
    };
    document.addEventListener('click', onClick, true);
    document.addEventListener('click', onPlainClick);
    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('click', onPlainClick);
    };
  }, [enabled]);

  const clear = useCallback(() => setSelected(new Set()), []);

  /**
   * Iter 156 — Marquee drag-to-select.
   * Mousedown on empty canvas → drag a rectangle → on mouseup, every
   * draggable element whose CENTER is inside the rectangle becomes
   * selected. Existing selection is replaced unless Shift was held.
   */
  useEffect(() => {
    if (!enabled) return;
    let startX = 0, startY = 0, started = false, additive = false;
    let marquee = null;

    const cleanup = () => {
      if (marquee) { marquee.remove(); marquee = null; }
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      started = false;
    };
    const onMove = (e) => {
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (!started) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        started = true;
        marquee = document.createElement('div');
        marquee.setAttribute('data-wb-marquee', '1');
        marquee.style.cssText = `
          position: fixed; z-index: 9001; pointer-events: none;
          border: 1.5px dashed rgba(59,130,246,0.85);
          background: rgba(59,130,246,0.10);
          border-radius: 2px;
        `;
        document.body.appendChild(marquee);
      }
      const x = Math.min(startX, e.clientX);
      const y = Math.min(startY, e.clientY);
      const w = Math.abs(dx), h = Math.abs(dy);
      marquee.style.left = `${x}px`;
      marquee.style.top  = `${y}px`;
      marquee.style.width  = `${w}px`;
      marquee.style.height = `${h}px`;
    };
    const onUp = (e) => {
      if (started && marquee) {
        const rect = marquee.getBoundingClientRect();
        const candidates = document.querySelectorAll('[data-testid^="inline-"], [data-wb-image="1"], [data-wb-button="1"]');
        const hits = new Set(additive ? selectedRef.current : []);
        candidates.forEach(el => {
          const r = el.getBoundingClientRect();
          const cx = r.left + r.width / 2;
          const cy = r.top + r.height / 2;
          if (cx >= rect.left && cx <= rect.right && cy >= rect.top && cy <= rect.bottom) {
            const k = keyOf(el);
            if (k) hits.add(k);
          }
        });
        setSelected(hits);
        // Swallow the upcoming `click` so it doesn't clear the freshly-
        // marqueed selection via the plain-click handler.
        const swallow = (cev) => {
          cev.preventDefault();
          cev.stopPropagation();
          cev.stopImmediatePropagation?.();
        };
        document.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => document.removeEventListener('click', swallow, { capture: true }), 250);
      }
      cleanup();
    };
    const onDown = (e) => {
      if (e.button !== 0) return;
      // Skip if user is starting on a draggable element, an overlay, or
      // any toolbar surface — those have their own pointer interactions.
      const t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('[data-testid^="inline-"], [data-wb-image="1"], [data-wb-button="1"]')) return;
      if (t.closest('[data-wb-overlay]')) return;
      if (t.closest('[data-wb-fluid-handle="1"]')) return;
      if (t.closest('.wb-multi-select-toolbar')) return;
      if (t.closest('.wb-section-resize-handle-portal')) return;
      // Only marquee while the click landed inside a canvas-rendered
      // section. (Clicking the global page header should not start one.)
      if (!t.closest('[data-block-id]')) return;
      startX = e.clientX;
      startY = e.clientY;
      additive = !!e.shiftKey;
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    };
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      cleanup();
    };
  }, [enabled]);

  /**
   * During a drag, group-move every selected element BY the same delta
   * (dCol, dRow). The element actively being dragged is excluded — its
   * own drag handler already applies the transform.
   */
  const dragSiblingsBy = useCallback((draggedKey, dCol, dRow, cellW) => {
    selectedRef.current.forEach(key => {
      if (key === draggedKey) return;
      const el = elFromKey(key);
      if (!el) return;
      el.style.setProperty('transform', `translate(${dCol * cellW}px, ${dRow * 60}px)`, 'important');
    });
  }, []);

  return { selected, clear, dragSiblingsBy };
}
